import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Gift, Plus, Edit, Trash2, Percent, Euro } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminPromotions = () => {
  const navigate = useNavigate();
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    discountType: 'percentage',
    discountValue: 0,
    isActive: true,
    validFrom: '',
    validUntil: '',
    applicableServices: ['transfer', 'hourly', 'corporate'],
    minAmount: 0
  });

  useEffect(() => {
    fetchPromotions();
  }, []);

  const fetchPromotions = async () => {
    try {
      const response = await axios.get(`${API}/api/promotions`);
      setPromotions(response.data);
    } catch (error) {
      console.error('Error fetching promotions:', error);
      toast.error('Erreur lors du chargement des promotions');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingPromo(null);
    setFormData({
      name: '',
      description: '',
      discountType: 'percentage',
      discountValue: 0,
      isActive: true,
      validFrom: '',
      validUntil: '',
      applicableServices: ['transfer', 'hourly', 'corporate'],
      minAmount: 0
    });
    setShowDialog(true);
  };

  const handleEdit = (promo) => {
    setEditingPromo(promo);
    setFormData({
      name: promo.name,
      description: promo.description || '',
      discountType: promo.discountType,
      discountValue: promo.discountValue,
      isActive: promo.isActive,
      validFrom: promo.validFrom ? promo.validFrom.split('T')[0] : '',
      validUntil: promo.validUntil ? promo.validUntil.split('T')[0] : '',
      applicableServices: promo.applicableServices,
      minAmount: promo.minAmount || 0
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      if (!formData.name || formData.discountValue <= 0) {
        toast.error('Veuillez remplir tous les champs obligatoires');
        return;
      }

      if (editingPromo) {
        await axios.put(`${API}/api/admin/promotions/${editingPromo.id}`, formData);
        toast.success('Promotion mise à jour avec succès');
      } else {
        await axios.post(`${API}/api/admin/promotions`, formData);
        toast.success('Promotion créée avec succès');
      }

      setShowDialog(false);
      fetchPromotions();
    } catch (error) {
      console.error('Error saving promotion:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (promoId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette promotion ?')) {
      return;
    }

    try {
      await axios.delete(`${API}/api/admin/promotions/${promoId}`);
      toast.success('Promotion supprimée');
      fetchPromotions();
    } catch (error) {
      console.error('Error deleting promotion:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleService = (service) => {
    setFormData(prev => ({
      ...prev,
      applicableServices: prev.applicableServices.includes(service)
        ? prev.applicableServices.filter(s => s !== service)
        : [...prev.applicableServices, service]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black to-gray-900 flex items-center justify-center">
        <p className="text-white text-xl">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/admin/dashboard')}
              variant="outline"
              className="border-gold/30 text-gold hover:bg-gold/10"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <h1 className="text-4xl font-bold text-white flex items-center gap-3">
              <Gift className="w-10 h-10 text-gold" />
              Gestion des Promotions
            </h1>
          </div>
          <Button
            onClick={handleCreate}
            className="bg-gradient-to-r from-gold to-amber-600 text-black hover:from-amber-600 hover:to-gold"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle Promotion
          </Button>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-gray-900 to-black border-gold/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Total Promotions</p>
                  <p className="text-3xl font-bold text-white">{promotions.length}</p>
                </div>
                <Gift className="w-12 h-12 text-gold/50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-gray-900 to-black border-gold/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Actives</p>
                  <p className="text-3xl font-bold text-green-500">
                    {promotions.filter(p => p.isActive).length}
                  </p>
                </div>
                <Gift className="w-12 h-12 text-green-500/50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-gray-900 to-black border-gold/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Inactives</p>
                  <p className="text-3xl font-bold text-gray-500">
                    {promotions.filter(p => !p.isActive).length}
                  </p>
                </div>
                <Gift className="w-12 h-12 text-gray-500/50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Promotions Table */}
        <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-gold/30">
          <CardHeader>
            <CardTitle className="text-2xl text-white">Liste des Promotions</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="border-gold/20 hover:bg-transparent">
                  <TableHead className="text-gold">Nom</TableHead>
                  <TableHead className="text-gold">Type</TableHead>
                  <TableHead className="text-gold">Valeur</TableHead>
                  <TableHead className="text-gold">Services</TableHead>
                  <TableHead className="text-gold">Validité</TableHead>
                  <TableHead className="text-gold">Statut</TableHead>
                  <TableHead className="text-gold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {promotions.map((promo) => (
                  <TableRow key={promo.id} className="border-gold/10 hover:bg-gold/5">
                    <TableCell>
                      <div>
                        <p className="text-white font-medium">{promo.name}</p>
                        {promo.description && (
                          <p className="text-gray-400 text-sm">{promo.description}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="border-gold/30 text-gold">
                        {promo.discountType === 'percentage' ? (
                          <><Percent className="w-3 h-3 mr-1" /> Pourcentage</>
                        ) : (
                          <><Euro className="w-3 h-3 mr-1" /> Montant Fixe</>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-white font-bold">
                      {promo.discountType === 'percentage' ? `${promo.discountValue}%` : `${promo.discountValue}€`}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {promo.applicableServices.map(service => (
                          <Badge key={service} variant="outline" className="border-blue-500/30 text-blue-400 text-xs">
                            {service === 'transfer' ? 'Transfert' : service === 'hourly' ? 'Par heure' : 'Événements'}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-400 text-sm">
                      {promo.validFrom || promo.validUntil ? (
                        <>
                          {promo.validFrom && <div>Du: {new Date(promo.validFrom).toLocaleDateString()}</div>}
                          {promo.validUntil && <div>Au: {new Date(promo.validUntil).toLocaleDateString()}</div>}
                        </>
                      ) : (
                        'Illimitée'
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={promo.isActive ? 'bg-green-500' : 'bg-gray-500'}>
                        {promo.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        <Button
                          onClick={() => handleEdit(promo)}
                          size="sm"
                          variant="outline"
                          className="border-gold/30 text-gold hover:bg-gold/10"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => handleDelete(promo.id)}
                          size="sm"
                          variant="outline"
                          className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {promotions.length === 0 && (
              <div className="text-center py-12">
                <Gift className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">Aucune promotion créée</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="bg-gray-900 border-gold/30 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl text-gold">
                {editingPromo ? 'Modifier la Promotion' : 'Nouvelle Promotion'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-white">Nom de la promotion *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-gray-800 border-gold/30 text-white"
                  placeholder="Ex: Réduction Printemps"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description" className="text-white">Description</Label>
                <textarea
                  id="description"
                  rows={2}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full bg-gray-800 border border-gold/30 text-white rounded-md p-3"
                  placeholder="Description de la promotion..."
                />
              </div>

              {/* Discount Type */}
              <div className="space-y-2">
                <Label className="text-white">Type de réduction *</Label>
                <Select
                  value={formData.discountType}
                  onValueChange={(value) => setFormData({ ...formData, discountType: value })}
                >
                  <SelectTrigger className="bg-gray-800 border-gold/30 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gold/30">
                    <SelectItem value="percentage">Pourcentage (%)</SelectItem>
                    <SelectItem value="fixed">Montant fixe (€)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Discount Value */}
              <div className="space-y-2">
                <Label htmlFor="discountValue" className="text-white">
                  Valeur de la réduction *
                  {formData.discountType === 'percentage' ? ' (%)' : ' (€)'}
                </Label>
                <Input
                  id="discountValue"
                  type="number"
                  min="0"
                  step="0.5"
                  value={formData.discountValue}
                  onChange={(e) => setFormData({ ...formData, discountValue: parseFloat(e.target.value) })}
                  className="bg-gray-800 border-gold/30 text-white"
                />
              </div>

              {/* Applicable Services */}
              <div className="space-y-2">
                <Label className="text-white">Services applicables</Label>
                <div className="flex gap-3">
                  {['transfer', 'hourly', 'corporate'].map(service => (
                    <div key={service} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={service}
                        checked={formData.applicableServices.includes(service)}
                        onChange={() => toggleService(service)}
                        className="w-4 h-4 rounded border-gold text-gold focus:ring-gold"
                      />
                      <label htmlFor={service} className="text-white cursor-pointer">
                        {service === 'transfer' ? 'Transfert' : service === 'hourly' ? 'Par heure' : 'Événements'}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Dates */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="validFrom" className="text-white">Valide à partir du</Label>
                  <Input
                    id="validFrom"
                    type="date"
                    value={formData.validFrom}
                    onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                    className="bg-gray-800 border-gold/30 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="validUntil" className="text-white">Valide jusqu'au</Label>
                  <Input
                    id="validUntil"
                    type="date"
                    value={formData.validUntil}
                    onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
                    className="bg-gray-800 border-gold/30 text-white"
                  />
                </div>
              </div>

              {/* Min Amount */}
              <div className="space-y-2">
                <Label htmlFor="minAmount" className="text-white">Montant minimum de réservation (€)</Label>
                <Input
                  id="minAmount"
                  type="number"
                  min="0"
                  value={formData.minAmount}
                  onChange={(e) => setFormData({ ...formData, minAmount: parseFloat(e.target.value) })}
                  className="bg-gray-800 border-gold/30 text-white"
                  placeholder="0 = aucun minimum"
                />
              </div>

              {/* Active Status */}
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="w-5 h-5 rounded border-gold text-gold focus:ring-gold"
                />
                <label htmlFor="isActive" className="text-lg font-semibold cursor-pointer">
                  Promotion active
                </label>
              </div>

              {/* Actions */}
              <div className="flex gap-3 justify-end pt-4">
                <Button
                  onClick={() => setShowDialog(false)}
                  variant="outline"
                  className="border-gray-600 text-gray-400"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleSave}
                  className="bg-gradient-to-r from-gold to-amber-600 text-black hover:from-amber-600 hover:to-gold"
                >
                  {editingPromo ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminPromotions;
